import { z } from 'zod';
import { logger } from '@/shared/lib/logger';

/**
 * 환경 변수 스키마 정의
 *
 * Zod를 사용하여 환경 변수를 런타임에 검증합니다.
 * 잘못된 설정은 앱 시작 시 즉시 에러로 표시됩니다.
 */
const envSchema = z.object({
  /** API 기본 URL */
  VITE_API_BASE_URL: z.string().url().default('http://localhost:8000'),

  /** API 요청 타임아웃 (밀리초, 1초~60초) */
  VITE_API_TIMEOUT: z.coerce.number().min(1000).max(60000).default(10000),

  /** OpenAPI 스펙 URL (Orval 코드 생성용) */
  VITE_OPENAPI_SPEC_URL: z.string().url().default('http://localhost:8000/api/openapi.json'),

  /** 애플리케이션 이름 */
  VITE_APP_NAME: z.string().default('AX Studio'),

  /** 애플리케이션 URL */
  VITE_APP_URL: z.string().url().default('https://store.ax.co.kr'),

  /** MSW 활성화 여부 (기본: 개발 모드에서 true) */
  VITE_ENABLE_MSW: z
    .string()
    .optional()
    .transform((val) => (val === undefined ? undefined : val === 'true')),

  /** Vite 개발 모드 여부 */
  DEV: z.coerce.boolean().default(true),

  /** Vite 프로덕션 모드 여부 */
  PROD: z.coerce.boolean().default(false),
});

/**
 * 환경 변수 파싱 및 검증
 *
 * 검증 실패 시 상세한 에러 메시지를 출력합니다.
 */
function parseEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const fieldErrors = result.error.flatten().fieldErrors;
    logger.error('Environment variable validation failed', undefined, {
      action: 'env_validation',
      metadata: { fieldErrors },
    });
    throw new Error('환경 변수 설정이 올바르지 않습니다. .env 파일을 확인하세요.');
  }

  return result.data;
}

const env = parseEnv();

/**
 * 환경 변수 중앙 관리
 *
 * 모든 환경 변수는 이 객체를 통해 접근합니다.
 * - Zod 스키마 기반 런타임 검증
 * - 타입 안전성 보장
 * - 기본값 중앙 관리
 * - 자동 완성 지원
 */
export const config = {
  /** API 기본 URL */
  apiBaseUrl: env.VITE_API_BASE_URL,

  /** API 요청 타임아웃 (밀리초) */
  apiTimeout: env.VITE_API_TIMEOUT,

  /** OpenAPI 스펙 URL (Orval 코드 생성용) */
  openapiSpecUrl: env.VITE_OPENAPI_SPEC_URL,

  /** 애플리케이션 이름 */
  appName: env.VITE_APP_NAME,

  /** 애플리케이션 URL */
  appUrl: env.VITE_APP_URL,

  /** MSW 활성화 여부 (VITE_ENABLE_MSW 명시 시 해당 값, 미설정 시 개발 모드에서 활성화) */
  enableMsw: env.VITE_ENABLE_MSW ?? env.DEV,

  /** 개발 모드 여부 */
  isDev: env.DEV,

  /** 프로덕션 모드 여부 */
  isProd: env.PROD,
} as const;

export type Config = typeof config;
